import { useAsync } from 'react-use-v13';
import React, { useEffect } from 'react';
import {
  UnleashVariant,
  unleashFeatureFlagEnabled,
  unleashFeatureFlagVariant,
} from '@scale/frontend-lib/featureFlag';
import qs from 'qs';
import { UnleashQuery } from '@scale/shared/types/endpoints/featureFlag/unleash';
import HTTPError from '@scale/frontend-lib/httpError';
import _ from 'lodash';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { addFeatureFlagEvaluation } from '@scale/frontend-lib/datadog';

const useUnleashFeatureFlag = (
  key: string,
  query: UnleashQuery,
  defaultValue: boolean | undefined,
  shouldMount: boolean = true,
): boolean | undefined => {
  const [value, setValue] = React.useState(defaultValue);
  const componentMounted = React.useRef(false);

  useEffect(() => {
    componentMounted.current = true;
    return () => {
      componentMounted.current = false;
    };
  }, []);

  useAsync(async () => {
    if (!shouldMount || (_.keys(query).length > 0 && _.filter(query, x => !x).length > 0)) {
      return;
    }

    const resp = await unleashFeatureFlagEnabled({
      feature: key,
      querystring: qs.stringify(_.pickBy(query)),
    });
    if (componentMounted.current) {
      addFeatureFlagEvaluation(key, resp);
      setValue(resp);
    }
  }, [
    query.projectId,
    query.subtaskId,
    query.catalogId,
    query.teamId,
    query.userId,
    query.courseId,
    query.customerId,
    query.organizationId,
    query.datapackId,
    shouldMount,
  ]);
  return value;
};
export default useUnleashFeatureFlag;

export const useUnleashFeatureFlagWithLoading = (
  key: string,
  query: UnleashQuery,
  defaultValue: boolean | undefined,
  shouldMount: boolean = true,
  queryOptions?: UseQueryOptions<boolean | undefined>,
): UseQueryResult<boolean | undefined> => {
  const featureFlag = useQuery<boolean | undefined>(
    ['unleash-feature', key, query],
    async () => {
      if (!shouldMount || (_.keys(query).length > 0 && _.filter(query, x => !x).length > 0)) {
        return defaultValue;
      }

      const resp = await unleashFeatureFlagEnabled({
        feature: key,
        querystring: qs.stringify(_.pickBy(query)),
      });

      return resp ?? defaultValue;
    },
    queryOptions,
  );

  // If refetch is disabled, we only fetch manually once on mount
  useEffect(() => {
    if (queryOptions?.enabled) {
      return;
    }
    featureFlag.refetch();
  }, []);

  return featureFlag;
};

/**
 * A hook that can be stubbed to not perform the fetch if `opts.shouldFetch` is false
 */
export const useUnleashBoolean = (
  key: string,
  query: UnleashQuery,
  opts?: { shortCircuit?: boolean; defaultValue?: boolean | undefined },
): boolean | undefined => {
  const [value, setValue] = React.useState<boolean | undefined>(opts?.defaultValue);

  useEffect(() => {
    if (opts?.shortCircuit) return;
    unleashFeatureFlagEnabled({
      feature: key,
      querystring: qs.stringify(_.pickBy(query)),
    })
      .then(setValue)
      .catch(error => {
        // Since customers can access Nucleus without being authenticated,
        // avoid throwing an Unauthorized error if user is not logged in.
        if (error instanceof HTTPError && error.status === 401) {
          setValue(opts?.defaultValue);
        } else {
          throw error;
        }
      });
  }, [query.projectId, opts?.shortCircuit, opts?.defaultValue, key, query]);

  return value;
};

export const useUnleashVariant = (
  key: string,
  query: UnleashQuery,
  defaultValue: UnleashVariant | undefined = undefined,
  shouldMount: boolean = true,
): UnleashVariant | undefined => {
  const [value, setValue] = React.useState(defaultValue);

  useAsync(async () => {
    if (!shouldMount || (_.keys(query).length > 0 && _.filter(query, x => !x).length > 0)) {
      return;
    }

    const resp = await unleashFeatureFlagVariant({
      feature: key,
      querystring: qs.stringify(_.pickBy(query)),
    });

    setValue(resp);
  }, [query.projectId, query.subtaskId, query.catalogId, query.teamId, query.userId, shouldMount]);
  return value;
};
